import { GCameraShakeController } from "../../../animationController";
import { Mesh } from "../../../basePass";
import { GAudioEngine, SoundSample } from "../../../core/audioEngine";
import { GRenderTargets } from "../../../core/renderTargets";
import { BindRenderTarget } from "../../../core/resourcesUtils";
import { GScreenDesc } from "../../../core/screenDesc";
import { Shader } from "../../../core/shaderUtils";
import { GTexturePool } from "../../../core/texturePool";
import { Vector3 } from "../../../core/types";
import { GLBindUniformBuffer, GLSetTexture } from "../../../helpers/glHelper";
import { CommonRenderingResources } from "../../../shaders/shaderConfig";
import { ShaderSourceFullscreenPassVS } from "../../../shaders/shaderPostProcess";
import { GetShaderSourceHallwayPostProcessPS } from "../../../shaders/shaderSDF";
import { SceneBase } from "../../scene";
import { GetSDFRenderUniformParametersList } from "../../sdf";
import { HallwayUpdateMain } from "./hallwayScript";
import { InitSDFDestrHallwayScene } from "./hallwaysSDFSceneDesc";

export class PayneSceneHallway extends SceneBase {
    static GInstance: PayneSceneHallway | null = null;

    ShaderBasePassHallway: Shader | null = null;
    SceneSDFDescHallway: string | null = null;
    HallwayMeshesArr: Mesh[] = [];

    ShaderFullscreenVFX: Shader;

    PaintTexture;

    bPyramidTwistEnabled = false;
    PyramidTwistAcceleration = 1.0;
    PyramidTwistAcceleration2 = 1.0;
    PyramidParamsMinBrightness = 0.0;
    PyramidParamsBrightness = 0.0;

    //0:Start
    //1:Question
    //2:Move to Pyramid
    //3:TV
    EHallwaySceneState = -1;
    CameraTransitionParam = 0;
    CameraTransitionParamHeight = 0;
    CameraTransitionParamHeight2 = 0;

    //Sounds
    SoundIntroPad: SoundSample = new SoundSample();
    SoundOceanWater: SoundSample = new SoundSample();
    SoundOceanSong: SoundSample = new SoundSample();
    SoundPyramid: SoundSample = new SoundSample();
    SoundPyramidInitialGain = 3.0;
    SoundFireLow: SoundSample = new SoundSample();
    SoundSelectUI: SoundSample = new SoundSample();
    SoundImpactLow: SoundSample = new SoundSample();
    SoundSlowMotionEnd: SoundSample = new SoundSample();

    PlayOceanSong() {
        const audioContext = GAudioEngine.Instance!.Context;
        if (audioContext) {
            this.SoundOceanSong.Play(audioContext);
        }
    }
    PlayPyramidSound() {
        const audioContext = GAudioEngine.Instance!.Context;
        if (audioContext) {
            this.SoundPyramid.Play(audioContext);

            if (this.SoundIntroPad.Gain) {
                this.SoundIntroPad.Stop();
                this.SoundIntroPad.Gain!.gain.value = 0.0;
            }
            if (this.SoundOceanWater.Gain) {
                this.SoundOceanWater.Stop();
                this.SoundOceanWater.Gain!.gain.value = 0.0;
            }
        }
    }
    PlaySelectUISound() {
        const audioContext = GAudioEngine.Instance!.Context;
        if (audioContext) {
            this.SoundSelectUI.Play(audioContext);
        }
    }

    bGuiltStageRunning = false;
    SoundOnGuiltOn() {
        this.bGuiltStageRunning = true;
        const audioContext = GAudioEngine.Instance!.Context;
        if (audioContext) {
            if (this.SoundFireLow.Buffer) {
                const offset = Math.random() * this.SoundFireLow.Buffer.duration * 0.5;
                this.SoundFireLow.Play(audioContext, false, offset);
            }

            if (this.SoundIntroPad.Gain) {
                this.SoundIntroPad.Gain!.gain.value = 0.0;
            }
        }
    }
    SoundOnGuiltOff() {
        this.bGuiltStageRunning = false;
        if (this.SoundFireLow.bIsPlaying) {
            this.SoundFireLow.Stop();
        }
    }

    async LoadSounds() {
        const audioEngine = GAudioEngine.Instance!;
        if (audioEngine.Context && audioEngine.MasterGain) {
            this.SoundIntroPad.Init(audioEngine.Context, "introPad.mp3", audioEngine.MasterGain, 1.0);
            this.SoundOceanWater.Init(audioEngine.Context, "oceanWater.mp3", audioEngine.MasterGain, 1.0);
            this.SoundOceanSong.Init(audioEngine.Context, "oceanSong.mp3", audioEngine.MasterGain, 0.75);
            this.SoundSelectUI.Init(audioEngine.Context, "selectUI.mp3", audioEngine.MasterGain, 1.0);
            this.SoundFireLow.Init(audioEngine.Context, "fireLow.mp3", audioEngine.MasterGain, 1.0);
            this.SoundImpactLow.Init(audioEngine.Context, "impactLow.mp3", audioEngine.MasterGain, 0.5);
            this.SoundSlowMotionEnd.Init(audioEngine.Context, "slowEnd.mp3", audioEngine.MasterGain, 0.5);
            this.SoundPyramid.Init(
                audioEngine.Context,
                "pyramid.mp3",
                audioEngine.MasterGain,
                this.SoundPyramidInitialGain,
            );
        }
    }

    RenderPostProcess(gl: WebGL2RenderingContext, curShader: Shader) {
        gl.useProgram(curShader.ShaderProgram);

        GLBindUniformBuffer(
            gl,
            SceneBase.UniformBuffer.BufferGPU!,
            curShader.ShaderProgram,
            curShader.UniformParametersLocationList.SceneUniformBuffer,
        );

        GLSetTexture(gl, curShader.UniformParametersLocationList.ProjectorTexture, this.PaintTexture, 1);

        gl.bindVertexArray(CommonRenderingResources.FullscreenPassVAO);

        gl.drawArrays(gl.TRIANGLES, 0, 3);
    }

    RedSplinePosArr: Vector3[] = [];
    RedSplineVelArr: Vector3[] = [];

    constructor(gl: WebGL2RenderingContext) {
        super();

        this.SceneSDFDescHallway = InitSDFDestrHallwayScene();

        /* this.ShaderBasePassHallway = new Shader(
            gl,
            GetShaderSourceBasePassMainVS(),
            GetShaderSourceBasePassMainPS(this.SceneSDFDescHallway),
            GetBasePassUniformParametersList,
        ); */

        this.ShaderFullscreenVFX = new Shader(
            gl,
            ShaderSourceFullscreenPassVS,
            GetShaderSourceHallwayPostProcessPS(this.SceneSDFDescHallway),
            GetSDFRenderUniformParametersList,
        );

        this.PaintTexture = GTexturePool.CreateTexture(gl, false, `paintRose3`, true, true, false, null, null);

        this.LoadSounds();
    }

    Render(gl: WebGL2RenderingContext) {
        HallwayUpdateMain(gl, this);

        super.OnUpdate(gl);

        GCameraShakeController.OnUpdate();

        BindRenderTarget(gl, GRenderTargets.SceneColorFramebuffer!, GScreenDesc.RenderTargetSize, true, true, true);

        /* if (0) {
            const basePass = GBasePass.GInstance!;
            basePass.Begin(gl, this.ShaderBasePassHallway!);

            for (const meshRef of this.HallwayMeshesArr) {
                meshRef.RegenerateMatrix();
                basePass.RenderMesh(gl, this.ShaderBasePassHallway!, meshRef, SceneBase.TextureEnvMapWithMips, 1, 0);
            }

            basePass.End(gl);
        } */

        if (1) {
            this.RenderPostProcess(gl, this.ShaderFullscreenVFX);
        }

        //gl.enable(gl.DEPTH_TEST);
        //gl.depthFunc(gl.LESS);
        //RenderRedSpline(gl, this);
        //gl.disable(gl.DEPTH_TEST);
    }
}
